import React, { type PropsWithChildren, useCallback } from 'react';

// Need to disable this because we need to separate the default import from the named bindings
// Otherwise we get this eslint error
// "A type-only import can specify a default import or named bindings, but not both.ts(1363)""
import type AnalyticsClient from '@atlassiansox/analytics-web-client';
// eslint-disable-next-line no-duplicate-imports
import {
	type OperationalEventPayload,
	tenantType,
	type TrackEventPayload,
	userType,
} from '@atlassiansox/analytics-web-client';

import { AnalyticsListener, type UIAnalyticsEvent } from '@atlaskit/analytics-next';

const ENGAGEMENT_PLATFORM_CHANNEL = 'engagementPlatform';

const isPromise = (value: any): value is Promise<any> => {
	return value && typeof value.then === 'function';
};

export const EPExposuresListener = ({
	children,
	analyticsClientInstance,
}: PropsWithChildren<{
	analyticsClientInstance?: AnalyticsClient | null | Promise<AnalyticsClient>;
}>): React.JSX.Element => {
	const handleEvent = useCallback(
		(event: UIAnalyticsEvent) => {
			if (!analyticsClientInstance) {
				return;
			}

			const { eventType, attributes, ...restPayload } = event.payload || {};
			const userId = restPayload?.userId || attributes?.userId;
			const tenantId = restPayload?.tenantId || attributes?.tenantId;

			if (eventType !== 'track' && eventType !== 'operational') {
				return;
			}
			if (!userId || !tenantId) {
				return;
			}

			// Handle Promise if analyticsClientInstance is a Promise
			const processEvent = (client: AnalyticsClient) => {
				// Update client with user and tenant info
				try {
					if (tenantId) {
						client.setTenantInfo(tenantType.CLOUD_ID, tenantId);
					}

					if (userId) {
						client.setUserInfo(userType.ATLASSIAN_ACCOUNT, userId);
					}
				} catch (error) {
					return;
				}

				// Build payload with tenantId at top level (userId is automatically included via setUserInfo)
				const gasPayload = {
					...(restPayload as TrackEventPayload | OperationalEventPayload),
					tenantId,
					...(attributes ? { attributes } : {}),
				};

				if (eventType === 'track') {
					client.sendTrackEvent(gasPayload);
				} else if (eventType === 'operational') {
					client.sendOperationalEvent(gasPayload);
				}
			};

			if (isPromise(analyticsClientInstance)) {
				analyticsClientInstance.then(processEvent);
			} else {
				processEvent(analyticsClientInstance);
			}
		},
		[analyticsClientInstance],
	);

	return (
		<AnalyticsListener channel={ENGAGEMENT_PLATFORM_CHANNEL} onEvent={handleEvent}>
			{children}
		</AnalyticsListener>
	);
};
